import React, { useState, useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import donationApi from "../../apis/donationApi";
import driverApi from "../../apis/driverApi";
import Loading from "../../components/Loading/Loading";
import styles from "./AdminDriverIncome.module.scss";
import DISTRICTS from "../../constants/district";
import { PACKAGE_TYPE, PACKAGE_TYPE_VER_2 } from "../../constants/constant";

const AdminDriverIncome = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);

  // Filters
  const todayStr = new Date().toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [selectedDriverId, setSelectedDriverId] = useState<string>("all");
  const [dateMode, setDateMode] = useState<"today" | "all" | "custom">("today");

  const getPartnerId = (): string => {
    return process.env.REACT_APP_ENV === "production" ? "01G486CQAV7VW2FK3DHZV1T2FG" : "01G4866BBJGYR1JH8E6RPYEW2C";
  };

  // Helper to get driver income per order
  const getOrderIncome = (order: any): number => {
    try {
      if (order.driverIncome !== undefined && order.driverIncome !== null && typeof order.driverIncome === "number") {
        return order.driverIncome;
      }
      const storedConfigs = JSON.parse(localStorage.getItem("driver_order_incomes") || "{}");
      const conf = storedConfigs[order.id];
      if (conf?.calculatedIncome !== undefined && conf?.calculatedIncome !== null) {
        return Number(conf.calculatedIncome);
      }
    } catch (e) {}
    return 0;
  };

  // Load Drivers and Orders fast
  const fetchDriversAndOrders = async () => {
    setLoading(true);
    try {
      const resDrivers = await driverApi.listDrivers(getPartnerId());
      if (resDrivers.data?.code === 0 && resDrivers.data?.data) {
        const driverList = resDrivers.data.data;
        setDrivers(driverList);

        // Fetch all completed and active orders for each driver concurrently (paginated per driver)
        const orderPromises = driverList.map(async (d: any) => {
          let driverOrders: any[] = [];
          let offset = 0;
          const limit = 100;
          let hasMore = true;

          while (hasMore) {
            try {
              const res = await donationApi.getDonations({
                filter: {
                  driverId: d.id,
                  statuses: ["done", "processing", "confirm"],
                },
                paging: {
                  limit: limit,
                  offset: offset,
                },
              });
              if (res.data?.code === 0 && res.data?.data?.userDonations) {
                const list = res.data.data.userDonations;
                driverOrders = [...driverOrders, ...list];
                if (list.length < limit || !res.data.data.nextPage) {
                  hasMore = false;
                } else {
                  offset += limit;
                }
              } else {
                hasMore = false;
              }
            } catch (e) {
              console.error(`Error loading orders for driver ${d.id}`, e);
              hasMore = false;
            }
          }
          return driverOrders;
        });

        const results = await Promise.all(orderPromises);
        const combined = results.flat();
        setOrders(combined);
      }
    } catch (err) {
      console.error("Error fetching data", err);
      enqueueSnackbar("Không thể tải danh sách tài xế và đơn hàng", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriversAndOrders();
  }, []);

  // Helper to format order date to local YYYY-MM-DD for accurate comparison
  const formatLocalDate = (isoStr?: string) => {
    if (!isoStr) return "";
    try {
      const d = new Date(isoStr);
      if (isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (e) {
      return "";
    }
  };

  // Filter orders by driver & date (strictly requiring status done)
  const filteredCompletedOrders = useMemo(() => {
    return orders.filter((o) => {
      // Must be a completed order
      if (o.status !== "done") return false;

      // Driver filter
      if (selectedDriverId !== "all") {
        if (o.driverId !== selectedDriverId) return false;
      }

      // Date filter
      if (dateMode === "all") return true;

      const dateToCompare = formatLocalDate(o.updatedAt) || formatLocalDate(o.collectDate) || formatLocalDate(o.createdAt);
      return dateToCompare === selectedDate;
    });
  }, [orders, selectedDriverId, selectedDate, dateMode]);

  // Aggregate stats
  const totalCompletedIncome = useMemo(() => {
    return filteredCompletedOrders.reduce((sum, o) => sum + getOrderIncome(o), 0);
  }, [filteredCompletedOrders]);

  // Aggregate stats per driver
  const driverStats = useMemo(() => {
    return drivers.map((d) => {
      // Read driver bank account info
      let bankInfo = null;
      try {
        const raw = localStorage.getItem(`driver_bank_${d.id}`);
        if (raw) bankInfo = JSON.parse(raw);
      } catch (e) {}

      const bankDisplay = d.bankAccount
        ? `${d.bankName || "NH"}: ${d.bankAccount} (${d.accountHolder || d.name})`
        : bankInfo
        ? `${bankInfo.bankName || "NH"}: ${bankInfo.bankAccount} (${bankInfo.accountHolder || d.name})`
        : "Chưa cập nhật";

      // Driver orders for the date selection
      const driverOrders = orders.filter((o) => {
        if (o.driverId !== d.id) return false;
        if (dateMode === "all") return true;
        const dateToCompare = formatLocalDate(o.updatedAt) || formatLocalDate(o.collectDate) || formatLocalDate(o.createdAt);
        return dateToCompare === selectedDate;
      });

      const doneOrders = driverOrders.filter((o) => o.status === "done");
      const dayIncome = doneOrders.reduce((sum, o) => sum + getOrderIncome(o), 0);

      return {
        driver: d,
        bankDisplay,
        totalOrders: driverOrders.length,
        doneCount: doneOrders.length,
        dayIncome,
      };
    });
  }, [drivers, orders, selectedDate, dateMode]);

  const handleQuickDate = (mode: "today" | "all") => {
    setDateMode(mode);
    if (mode === "today") {
      setSelectedDate(todayStr);
    }
  };

  const getDriverName = (driverId: string) => {
    const found = drivers.find((d) => d.id === driverId);
    return found ? found.name : driverId ? `Tài xế (#${driverId.slice(-4)})` : "Chưa gán";
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Quản lý thu nhập tài xế theo ngày</h2>
        <p className={styles.subtitle}>
          Theo dõi số lượng đơn hàng mỗi tài xế chạy trong ngày, tính thù lao thực tế và thông tin chuyển khoản thanh toán.
        </p>
      </div>

      {/* Filter Bar */}
      <div className={styles.filtersBar}>
        <div className={styles.filterGroup}>
          <label>Chọn ngày quan sát</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setDateMode("custom");
            }}
          />
        </div>

        <div className={styles.quickFilters}>
          <button
            type="button"
            className={dateMode === "today" ? styles.active : ""}
            onClick={() => handleQuickDate("today")}
          >
            Hôm nay
          </button>
          <button
            type="button"
            className={dateMode === "all" ? styles.active : ""}
            onClick={() => handleQuickDate("all")}
          >
            Tất cả các ngày
          </button>
        </div>

        <div className={styles.filterGroup} style={{ minWidth: 200 }}>
          <label>Chọn tài xế</label>
          <select
            value={selectedDriverId}
            onChange={(e) => setSelectedDriverId(e.target.value)}
          >
            <option value="all">-- Tất cả tài xế ({drivers.length}) --</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({d.phone})
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => {
            fetchDriversAndOrders();
          }}
          style={{
            marginLeft: "auto",
            padding: "8px 16px",
            background: "#f1f5f9",
            border: "1px solid #cbd5e1",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: 600,
            color: "#334155",
          }}
        >
          🔄 Làm mới dữ liệu
        </button>
      </div>

      {/* Summary KPI Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.card}>
          <div className={styles.cardTitle}>Tổng tiền cần trả tài xế</div>
          <div className={styles.cardValue} style={{ color: "#16a34a" }}>
            {totalCompletedIncome.toLocaleString("vi")} VNĐ
          </div>
          <div className={styles.cardSub}>
            {filteredCompletedOrders.length} đơn hoàn thành {dateMode === "all" ? "toàn thời gian" : `ngày ${selectedDate}`}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>Tổng đơn đã giao thành công</div>
          <div className={styles.cardValue} style={{ color: "#2563eb" }}>
            {filteredCompletedOrders.length} đơn
          </div>
          <div className={styles.cardSub}>
            {selectedDriverId === "all" ? "Tất cả tài xế" : getDriverName(selectedDriverId)}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>Tổng đơn tài xế đã nhận</div>
          <div className={styles.cardValue} style={{ color: "#f59e0b" }}>
            {orders.filter((o) => o.driverId && (selectedDriverId === "all" || o.driverId === selectedDriverId)).length} đơn
          </div>
          <div className={styles.cardSub}>Tất cả các đơn đã gán cho tài xế</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>Tài xế đang hoạt động</div>
          <div className={styles.cardValue} style={{ color: "#64748b" }}>
            {drivers.filter((d) => d.status !== "DISABLED").length} / {drivers.length}
          </div>
          <div className={styles.cardSub}>Tài xế trong hệ thống</div>
        </div>
      </div>

      {/* Driver Summary Cards (For Quick Payout & Monitoring) */}
      <div className={styles.driverSummarySection}>
        <div className={styles.sectionTitle}>
          <span>Bảng tổng kết theo từng tài xế ({dateMode === "all" ? "Toàn thời gian" : `Ngày ${selectedDate}`})</span>
          <span style={{ fontSize: 13, color: "#64748b", fontWeight: "normal" }}>
            (Nhấp vào tài xế để lọc chi tiết các đơn bên dưới)
          </span>
        </div>

        <div className={styles.driverCardsGrid}>
          {driverStats.map((item) => {
            const isSelected = selectedDriverId === item.driver.id;
            return (
              <div
                key={item.driver.id}
                className={`${styles.driverCard} ${isSelected ? styles.selected : ""}`}
                onClick={() => setSelectedDriverId(isSelected ? "all" : item.driver.id)}
              >
                <div className={styles.driverHeader}>
                  <div>
                    <div className={styles.name}>{item.driver.name}</div>
                    <div className={styles.phone}>📞 {item.driver.phone}</div>
                  </div>
                  <span className={styles.badge}>{item.doneCount} đơn xong</span>
                </div>

                <div className={styles.bankInfo}>
                  <div className={styles.bankTitle}>💳 Thông tin thanh toán:</div>
                  <div>{item.bankDisplay}</div>
                </div>

                <div className={styles.incomeRow}>
                  <span className={styles.label}>Tổng thù lao ngày:</span>
                  <span className={styles.amount}>+{item.dayIncome.toLocaleString("vi")} VNĐ</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detailed Order List */}
      <div className={styles.tableSection}>
        <div className={styles.sectionTitle}>
          <span>
            Chi tiết các đơn hàng hoàn thành của tài xế
            {selectedDriverId !== "all" && ` - Tài xế: ${getDriverName(selectedDriverId)}`}
            {dateMode !== "all" && ` (${selectedDate})`}
          </span>
          <span style={{ fontSize: 13, color: "#16a34a", fontWeight: "bold" }}>
            Tổng cộng: {filteredCompletedOrders.length} đơn | +{totalCompletedIncome.toLocaleString("vi")} VNĐ
          </span>
        </div>

        {loading ? (
          <Loading />
        ) : filteredCompletedOrders.length === 0 ? (
          <div className={styles.empty}>
            Không tìm thấy đơn hàng hoàn thành nào của tài xế trong ngày hoặc bộ lọc đã chọn.
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Mã đơn</th>
                  <th>Tài xế giao</th>
                  <th>Khách hàng & SĐT</th>
                  <th>Địa chỉ lấy hàng</th>
                  <th>Khối lượng</th>
                  <th>Thời gian hoàn thành</th>
                  <th>Ảnh giao nhận</th>
                  <th>Thù lao tài xế</th>
                </tr>
              </thead>
              <tbody>
                {filteredCompletedOrders.map((o) => {
                  let proof: any = null;
                  try {
                    const storedProofs = JSON.parse(localStorage.getItem("order_delivery_proofs") || "{}");
                    proof = storedProofs[o.id];
                  } catch (e) {}

                  const income = getOrderIncome(o);

                  return (
                    <tr key={o.id}>
                      <td>
                        <b>{o.code}</b>
                      </td>
                      <td>
                        <span style={{ fontWeight: 600, color: "#0369a1" }}>
                          {getDriverName(o.driverId)}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{o.customerName}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>{o.customerPhone}</div>
                      </td>
                      <td style={{ maxWidth: 220 }}>
                        {o.customerAddress?.address || "N/A"}
                      </td>
                      <td>
                        {(() => {
                          if (!o.services || o.services.length === 0) {
                            const rootPt = o.packageType;
                            const foundRoot = PACKAGE_TYPE.find((p: any) => p.packageType === rootPt);
                            const foundRoot2 = PACKAGE_TYPE_VER_2.find((p: any) => p.packageTypeId === rootPt);
                            return foundRoot?.label || foundRoot2?.label || rootPt || "N/A";
                          }

                          let totalMin = 0;
                          let totalMax = 0;
                          let hasValidWeight = false;

                          o.services.forEach((svc: any) => {
                            const pt = svc.packageType || (svc as any).package_type;
                            const pId = (svc as any).packageTypeId || (svc as any).packageTypeID;
                            let label = "";
                            if (pt) {
                              label = PACKAGE_TYPE.find((p: any) => p.packageType === pt)?.label || pt;
                            } else if (pId) {
                              label = PACKAGE_TYPE_VER_2.find((p: any) => p.packageTypeId === pId)?.label || pId;
                            }

                            if (label) {
                              const nums = label.match(/\d+/g);
                              if (nums && nums.length >= 2) {
                                totalMin += parseInt(nums[0], 10);
                                totalMax += parseInt(nums[1], 10);
                                hasValidWeight = true;
                              }
                            }
                          });

                          if (hasValidWeight) {
                            return `${totalMin} đến ${totalMax} kg`;
                          }

                          const rootPt = o.packageType;
                          const foundRoot = PACKAGE_TYPE.find((p: any) => p.packageType === rootPt);
                          const foundRoot2 = PACKAGE_TYPE_VER_2.find((p: any) => p.packageTypeId === rootPt);
                          return foundRoot?.label || foundRoot2?.label || rootPt || "N/A";
                        })()}
                      </td>
                      <td>
                        {o.updatedAt
                          ? new Date(o.updatedAt).toLocaleString("vi-VN", {
                              hour: "2-digit",
                              minute: "2-digit",
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "N/A"}
                      </td>
                      <td>
                        {proof?.pickupImage || proof?.deliveryImage ? (
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            {proof.pickupImage && (
                              <a
                                href={proof.pickupImage}
                                target="_blank"
                                rel="noreferrer"
                                title="Ảnh nhận hàng lúc tài xế đến lấy"
                              >
                                <img
                                  src={proof.pickupImage}
                                  alt="Nhận hàng"
                                  style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 6,
                                    objectFit: "cover",
                                    border: "1px solid #16a34a",
                                  }}
                                />
                              </a>
                            )}
                            {proof.deliveryImage && (
                              <a
                                href={proof.deliveryImage}
                                target="_blank"
                                rel="noreferrer"
                                title="Ảnh trả hàng lúc giao tại điểm tập kết/kho"
                              >
                                <img
                                  src={proof.deliveryImage}
                                  alt="Trả hàng"
                                  style={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 6,
                                    objectFit: "cover",
                                    border: "1px solid #2563eb",
                                  }}
                                />
                              </a>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: 12, color: "#94a3b8", fontStyle: "italic" }}>
                            Chưa có ảnh
                          </span>
                        )}
                      </td>
                      <td style={{ color: "#16a34a", fontWeight: 700, fontSize: 15 }}>
                        +{income.toLocaleString("vi")} VNĐ
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDriverIncome;
